import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthBrandHeading } from '@/auth/auth-brand-heading';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/auth/auth-store';
import { loginByCode } from '@/auth/auth-api';
import { isBlank, isFlutterEmail } from '@/auth/email-validation';
import { safeLogAuthError } from '@/auth/log-helpers';

/**
 * Sign Up with Access Code (Phase 5 of PROD-892).
 *
 * Flutter source:
 *   - `sign_up_with_access_code/presentation/pages/signup_with_access_code_screen.dart`
 *   - `sign_up_with_access_code/presentation/widgets/body_widgets.dart` — email + code fields
 *   - `sign_up_with_access_code/presentation/widgets/header_widgets.dart` — "Sign up with access code" / subtitle
 *   - `sign_up_with_access_code/presentation/bloc/signup_with_access_code_bloc.dart`
 *   - `sign_up_with_access_code/data/datasources/sign_up_data_source_impl.dart` — POST `/api/Account/LoginByCode`
 *
 * **Audit-drift from briefing**: The briefing said Phase 5 uses
 * `isresetpassword: false`. Flutter `sign_up_data_source_impl.dart:27`
 * hardcodes `isresetpassword: true` for BOTH the signup flow AND the
 * reset-password access-code flow (they share the same useCase). Per
 * CLAUDE.md Dart-source-authoritative rule, we send `true` too.
 *
 * **Audit-drift on email validation string**: Phase 5 uses
 * `S.of(context).notAValidEmail` ("Not a valid email") rather than
 * Phases 1/2's `emailIsNotValid` ("Email address is not valid"). Preserved
 * verbatim per the magic-strings rule.
 *
 * On success → `/create-password?email=<>&token=<>` (Phase 4).
 */
export function SignupWithAccessCodePage() {
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (status === 'authenticated' || status === 'refreshing') {
    return <Navigate to="/" replace />;
  }

  const showEmailError = !isBlank(email) && !isFlutterEmail(email);
  const canSubmit =
    isFlutterEmail(email) && !isBlank(code) && !isSubmitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const response = await loginByCode({
        email,
        code,
        // Flutter hardcodes `isresetpassword: true` for the signup flow
        // too (sign_up_data_source_impl.dart:27); both flows share the
        // same useCase. Preserved verbatim per Dart-source rule.
        isResetPassword: true,
      });
      if (!response.passwordResetToken) {
        toast.error('Failed to signup');
        return;
      }
      const targetEmail = response.emailAddress ?? email;
      navigate(
        `/create-password?email=${encodeURIComponent(targetEmail)}&token=${encodeURIComponent(response.passwordResetToken)}`,
      );
    } catch (err) {
      safeLogAuthError('Signup with access code', err);
      toast.error('Failed to signup');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sidebar px-4">
      <form
        onSubmit={onSubmit}
        className="w-90 max-w-full rounded-lg border border-border-default bg-surface-card p-8 flex flex-col gap-4"
        aria-label="Sign up with access code"
      >
        <AuthBrandHeading
          title="Sign up with access code"
          subtitle="Enter access code from the invite sent by your organization"
        />

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="signup-email"
            className="text-xs font-medium text-text-secondary"
          >
            Email address
          </Label>
          <Input
            id="signup-email"
            type="text"
            inputMode="email"
            autoComplete="username"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={showEmailError || undefined}
            aria-describedby={
              showEmailError ? 'signup-email-error' : undefined
            }
            disabled={isSubmitting}
          />
          {showEmailError && (
            <span
              id="signup-email-error"
              role="alert"
              className="text-sm text-status-error"
            >
              Not a valid email
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label
            htmlFor="signup-code"
            className="text-xs font-medium text-text-secondary"
          >
            Code
          </Label>
          <Input
            id="signup-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Enter code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <Button type="submit" disabled={!canSubmit} className="mt-2">
          {isSubmitting ? 'Verifying…' : 'Next'}
        </Button>

        <Link
          to="/login"
          className="self-center text-sm font-medium text-brand-primary hover:underline"
        >
          Log in
        </Link>
      </form>
    </div>
  );
}

export default SignupWithAccessCodePage;
